import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Sparkles, LayoutList } from 'lucide-react';
import { LandingSplitSectionItem, createSplitSection, updateSplitSection } from '../../services/landing/landingSectionsService';

interface SplitSectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    itemToEdit?: LandingSplitSectionItem | null;
}

export const SplitSectionModal: React.FC<SplitSectionModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    itemToEdit,
}) => {
    const [tag, setTag] = useState('Para todos');
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [caracteristicas, setCaracteristicas] = useState('');
    const [imagenIzquierda, setImagenIzquierda] = useState(true);
    const [colorAcento, setColorAcento] = useState('#61257B');
    const [textoBoton, setTextoBoton] = useState('Obtener más información →');
    const [enlaceBoton, setEnlaceBoton] = useState('/espacios/coworking');
    const [urlImagen, setUrlImagen] = useState('');
    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [orden, setOrden] = useState<number>(0);
    const [activo, setActivo] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (itemToEdit) {
            setTag(itemToEdit.tag || 'Para todos');
            setTitulo(itemToEdit.titulo);
            setDescripcion(itemToEdit.descripcion);
            setCaracteristicas(itemToEdit.caracteristicas || '');
            setImagenIzquierda(itemToEdit.imagen_izquierda);
            setColorAcento(itemToEdit.color_acento || '#61257B');
            setTextoBoton(itemToEdit.texto_boton || 'Obtener más información →');
            setEnlaceBoton(itemToEdit.enlace_boton || '/espacios/coworking');
            setUrlImagen(itemToEdit.url_imagen || '');
            setOrden(itemToEdit.orden);
            setActivo(itemToEdit.activo);
            setPreviewUrl(itemToEdit.url_imagen || itemToEdit.imagen || null);
            setImagenFile(null);
        } else {
            setTag('Para todos');
            setTitulo('');
            setDescripcion('');
            setCaracteristicas('Membresías flexibles\nOficinas llave en mano\nComunidad activa\nSoporte 24/7');
            setImagenIzquierda(true);
            setColorAcento('#61257B');
            setTextoBoton('Obtener más información →');
            setEnlaceBoton('/espacios/coworking');
            setUrlImagen('');
            setOrden(0);
            setActivo(true);
            setImagenFile(null);
            setPreviewUrl(null);
        }
        setError(null);
    }, [itemToEdit, isOpen]);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImagenFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!titulo.trim()) {
            setError('El título de la sección es obligatorio');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('tag', tag.trim());
            formData.append('titulo', titulo.trim());
            formData.append('descripcion', descripcion.trim());
            formData.append('caracteristicas', caracteristicas.trim());
            formData.append('imagen_izquierda', imagenIzquierda ? 'true' : 'false');
            formData.append('color_acento', colorAcento.trim());
            formData.append('texto_boton', textoBoton.trim());
            formData.append('enlace_boton', enlaceBoton.trim());
            formData.append('url_imagen', urlImagen.trim());
            formData.append('orden', String(orden));
            formData.append('activo', activo ? 'true' : 'false');

            if (imagenFile) {
                formData.append('imagen', imagenFile);
            }

            if (itemToEdit) {
                await updateSplitSection(itemToEdit.id, formData);
            } else {
                await createSplitSection(formData);
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error al guardar sección dividida:', err);
            setError(err.response?.data?.detail || 'Error al guardar la sección informativa');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <div className="relative w-full max-w-xl bg-white dark:bg-[#120a21] rounded-2xl shadow-2xl border border-gray-100 dark:border-purple-900/40 p-6 my-8">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                >
                    <X size={18} />
                </button>

                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-tr from-[#61257B]/20 to-[#c1348b]/20 text-[#c1348b] rounded-xl border border-purple-500/20">
                        <LayoutList size={22} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {itemToEdit ? 'Editar Sección Dividida' : 'Nueva Sección Dividida (Split Section)'}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Bloque informativo con foto, viñetas de valor y llamada a la acción
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl text-red-600 dark:text-red-400 text-xs">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Etiqueta / Subtítulo Superior
                            </label>
                            <input
                                type="text"
                                value={tag}
                                onChange={(e) => setTag(e.target.value)}
                                placeholder="Para todos / Empresas"
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Orientación de la Imagen
                            </label>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setImagenIzquierda(true)}
                                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                                        imagenIzquierda
                                            ? 'bg-[#61257B] text-white border-[#61257B] shadow-md shadow-purple-900/20'
                                            : 'bg-white dark:bg-[#190f2b] text-gray-700 dark:text-gray-300 border-gray-200 dark:border-purple-900/40'
                                    }`}
                                >
                                    🖼️ Izquierda
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setImagenIzquierda(false)}
                                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold border transition-all ${
                                        !imagenIzquierda
                                            ? 'bg-[#61257B] text-white border-[#61257B] shadow-md shadow-purple-900/20'
                                            : 'bg-white dark:bg-[#190f2b] text-gray-700 dark:text-gray-300 border-gray-200 dark:border-purple-900/40'
                                    }`}
                                >
                                    Derecha 🖼️
                                </button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Título Principal *
                        </label>
                        <input
                            type="text"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            placeholder="Ej: Espacio de trabajo simplificado"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Descripción
                        </label>
                        <textarea
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            rows={3}
                            placeholder="Independientemente de tu presupuesto o necesidad..."
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Características o Beneficios (un punto por cada línea)
                        </label>
                        <textarea
                            value={caracteristicas}
                            onChange={(e) => setCaracteristicas(e.target.value)}
                            rows={3}
                            placeholder={`Membresías flexibles\nOficinas llave en mano\nComunidad activa\nSoporte 24/7`}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:border-[#c1348b]"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto del Botón
                            </label>
                            <input
                                type="text"
                                value={textoBoton}
                                onChange={(e) => setTextoBoton(e.target.value)}
                                placeholder="Obtener más información →"
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-xs focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Enlace de Destino
                            </label>
                            <input
                                type="text"
                                value={enlaceBoton}
                                onChange={(e) => setEnlaceBoton(e.target.value)}
                                placeholder="/espacios/coworking"
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-xs focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                    </div>

                    {/* Subida de Imagen */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Fotografía de la Sección
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-4 pb-5 border-2 border-gray-300 dark:border-purple-900/50 border-dashed rounded-xl hover:border-[#61257B] transition-colors bg-gray-50/50 dark:bg-purple-950/10">
                            <div className="space-y-2 text-center">
                                {previewUrl ? (
                                    <div className="relative inline-block">
                                        <img
                                            src={previewUrl}
                                            alt="Vista previa"
                                            className="h-32 w-auto object-cover rounded-lg shadow-md mx-auto"
                                        />
                                        <span className="block mt-2 text-xs text-purple-600 font-semibold">
                                            {imagenFile ? `Archivo: ${imagenFile.name}` : 'Imagen actual'}
                                        </span>
                                    </div>
                                ) : (
                                    <ImageIcon className="mx-auto h-10 w-10 text-gray-400" />
                                )}
                                <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                                    <label className="relative cursor-pointer rounded-md font-semibold text-[#61257B] dark:text-purple-400 hover:underline">
                                        <span>Subir foto desde tu dispositivo</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="sr-only"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">PNG, JPG, WEBP hasta 10MB</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-center pt-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Posición / Orden
                            </label>
                            <input
                                type="number"
                                min={0}
                                value={orden}
                                onChange={(e) => setOrden(Number(e.target.value))}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activo}
                                    onChange={(e) => setActivo(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-purple-950 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c1348b]"></div>
                            </label>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {activo ? 'Publicado' : 'Oculto'}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-purple-900/30">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-purple-950/40 rounded-xl transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[#61257B] to-[#c1348b] hover:opacity-95 rounded-xl shadow-lg shadow-purple-900/20 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <span className="inline-block w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    Guardando...
                                </>
                            ) : itemToEdit ? (
                                'Actualizar Sección'
                            ) : (
                                'Guardar Sección'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
